/**
 * LeftShield Portal - Security Page
 * Displays security findings, vulnerabilities, and scan results
 *
 * Migrated onto the List & Grid Standard — see `.specify/journals/list-grid-adoption-journal.md`
 * (§ MIGRATION RECIPE) and the committed reference `src/pages/ApiKeysPage.tsx`. All three grids
 * (Findings, Vulnerabilities, Scans) share ONE `useListUrlState` instance with tab-scoped sort
 * fallback (the `AttestationsPage` pattern). Chevrons now route to the GUID-based detail pages
 * (#12746 Wave 2) — `/security/findings/:referenceId`, `/security/vulnerabilities/:referenceId`,
 * `/runs/:referenceId` — replacing the former local-state `DetailDrawer` instances. The finding
 * suppression-request affordance moved to `FindingDetailPage.tsx` alongside the rest of the
 * finding detail sections.
 */
import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Title2,
  Title3,
  Body1,
  Body2,
  Text,
  Card,
  CardHeader,
  Badge,
  tokens,
  Spinner,
  Button,
  Tab,
  TabList,
  SelectTabEvent,
  SelectTabData,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Input,
  Dropdown,
  Option,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Checkbox,
  Divider,
  makeStyles,
} from '@fluentui/react-components';
import {
  BugRegular,
  WarningRegular,
  ShieldCheckmarkRegular,
  FilterRegular,
  ArrowSyncRegular,
  ChevronRightRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { findingsApi, vulnerabilitiesApi, runsApi, dashboardApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import type { Finding, Vulnerability, Run, Severity, FindingStatus } from '../types';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import { PageHeader } from '../components/common/PageHeader';
import {
  ListPageLayout,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

type TabValue = 'findings' | 'vulnerabilities' | 'scans';

/** Sortable columns per tab (only columns that carried a `compare` fn on the legacy DataGrid). */
const FINDINGS_SORT_FIELDS: readonly string[] = ['title'];
const VULNERABILITY_SORT_FIELDS: readonly string[] = ['identifier'];
const RUN_SORT_FIELDS: readonly string[] = ['name'];

const getSeverityColor = (severity: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (severity?.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

const getStatusColor = (status: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (status?.toLowerCase()) {
    case 'open':
      return 'danger';
    case 'in_progress':
    case 'confirmed':
      return 'warning';
    case 'resolved':
    case 'closed':
      return 'success';
    default:
      return 'informative';
  }
};

interface AdvancedFilters {
  dateRangeFrom: string;
  dateRangeTo: string;
  frameworkCodes: string[];
  findingTypes: string[];
  repositories: string[];
  hasRemediation: boolean | null;
  isKnownExploited: boolean | null;
  assignedToMe: boolean;
}

/** Narrow an unknown React Query error down to the shape the unauthenticated-banner check needs. */
function isUnauthorizedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { status, message } = error as { status?: number; message?: string };
  return status === 401 || (typeof message === 'string' && message.includes('MSAL'));
}

/** Severity (+ Status, findings-only) are encoded together into the shared `filters` URL param
 *  (§17/§19) — `field:op:value` pairs joined with `;`, same convention as `PolicyRunsPage`. */
function parseSecurityFilters(raw: string): { severity: Severity | ''; status: FindingStatus | '' } {
  let severity: Severity | '' = '';
  let status: FindingStatus | '' = '';
  if (raw) {
    for (const part of raw.split(';')) {
      const [field, , value] = part.split(':');
      if (field === 'severity' && value) severity = value as Severity;
      if (field === 'status' && value) status = value as FindingStatus;
    }
  }
  return { severity, status };
}

function serializeSecurityFilters(severity: Severity | '', status: FindingStatus | ''): string {
  const parts: string[] = [];
  if (severity) parts.push(`severity:eq:${severity}`);
  if (status) parts.push(`status:eq:${status}`);
  return parts.join(';');
}

/** Wraps the (server-paginated) `findingsApi.getAll` response as a `PagedResult` — severity/status
 *  are sent server-side (baked into the fetch closure + query key, like `ApiKeysPage`'s
 *  `showRevoked`); `useAdaptiveGridData` then applies search/sort/paging in memory (client mode). */
async function fetchFindingsPage(
  severity: Severity | '',
  status: FindingStatus | '',
  params: ListQueryParams
): Promise<PagedResult<Finding>> {
  return findingsApi.getAll({ severity: severity || undefined, status: status || undefined }, 1, params.pageSize);
}

/** Wraps the (server-paginated) `vulnerabilitiesApi.getAll` response as a `PagedResult` — severity
 *  is sent server-side; `useAdaptiveGridData` applies search/sort/paging in memory (client mode). */
async function fetchVulnerabilitiesPage(
  severity: Severity | '',
  params: ListQueryParams
): Promise<PagedResult<Vulnerability>> {
  return vulnerabilitiesApi.getAll({ severity: severity || undefined }, 1, params.pageSize);
}

/** Wraps the (unpaged, limit-capped) `runsApi.getRecent` response as a `PagedResult` —
 *  `useAdaptiveGridData` then applies search/sort/paging in memory (client mode), matching the
 *  pre-migration behavior where the Scans tab only ever loaded the 20 most recent runs. */
async function fetchRunsPage(params: ListQueryParams): Promise<PagedResult<Run>> {
  const items = await runsApi.getRecent(20);
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
});

export const SecurityPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState<TabValue>('findings');
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);

  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    dateRangeFrom: '',
    dateRangeTo: '',
    frameworkCodes: [],
    findingTypes: [],
    repositories: [],
    hasRemediation: null,
    isKnownExploited: null,
    assignedToMe: false,
  });

  const { data: dashboard, isLoading: dashboardLoading, error: dashboardError } = useQuery({
    queryKey: ['securityDashboard'],
    queryFn: () => dashboardApi.getSecurityDashboard(),
  });

  // List view state (§19) — page/sort/search/filters all persist to the URL, shared across all
  // three tabs (each tab reads back only the sort field it recognises — see `sortFieldRaw` below).
  const { state, setPage, setSort, setSearch, setFilters } = useListUrlState({ defaultSort: 'title:asc' });

  const { severity: severityFilter, status: statusFilter } = parseSecurityFilters(state.filters);
  const handleSeverityFilterChange = (value: Severity | '') =>
    setFilters(serializeSecurityFilters(value, statusFilter));
  const handleStatusFilterChange = (value: FindingStatus | '') =>
    setFilters(serializeSecurityFilters(severityFilter, value));

  const [sortFieldRaw, sortDirRaw] = (state.sort ?? 'title:asc').split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';

  // Each tab has its own sortable field; fall back to that tab's own default when the URL's sort
  // field belongs to a DIFFERENT tab (e.g. just switched tabs) — never sorts by a field the active
  // grid doesn't have.
  const findingsSortField = FINDINGS_SORT_FIELDS.includes(sortFieldRaw) ? sortFieldRaw : 'title';
  const vulnerabilitiesSortField = VULNERABILITY_SORT_FIELDS.includes(sortFieldRaw) ? sortFieldRaw : 'identifier';
  const runsSortField = RUN_SORT_FIELDS.includes(sortFieldRaw) ? sortFieldRaw : 'name';
  const findingsSortState: SortState[] = [{ field: findingsSortField, dir: sortDir }];
  const vulnerabilitiesSortState: SortState[] = [{ field: vulnerabilitiesSortField, dir: sortDir }];
  const runsSortState: SortState[] = [{ field: runsSortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortFieldRaw) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortFieldRaw) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const findingsGrid = useAdaptiveGridData<Finding>({
    queryKeyBase: ['security-findings', severityFilter, statusFilter],
    fetchPage: (params) => fetchFindingsPage(severityFilter, statusFilter, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['title', 'controlId', 'frameworkCode'],
    sortState: findingsSortState,
    enabled: selectedTab === 'findings',
  });

  const vulnerabilitiesGrid = useAdaptiveGridData<Vulnerability>({
    queryKeyBase: ['security-vulnerabilities', severityFilter],
    fetchPage: (params) => fetchVulnerabilitiesPage(severityFilter, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['identifier', 'title'],
    sortState: vulnerabilitiesSortState,
    enabled: selectedTab === 'vulnerabilities',
  });

  const runsGrid = useAdaptiveGridData<Run>({
    queryKeyBase: ['security-runs'],
    fetchPage: fetchRunsPage,
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['name', 'targetIdentifier'],
    sortState: runsSortState,
    enabled: selectedTab === 'scans',
  });

  const activeGrid =
    selectedTab === 'findings' ? findingsGrid : selectedTab === 'vulnerabilities' ? vulnerabilitiesGrid : runsGrid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after a tab switch,
  // search, or filter change.
  useEffect(() => {
    if (!activeGrid.isLoading && state.page > activeGrid.totalPages) {
      setPage(activeGrid.totalPages);
    }
  }, [activeGrid.isLoading, activeGrid.totalPages, state.page, setPage]);

  const handleTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabValue);
  };

  if (dashboardLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading security data..." />
      </div>
    );
  }

  // Auth error detection
  if (isUnauthorizedError(dashboardError)) {
    return (
      <div className={styles.page}>
        <PageHeader title="Security" subtitle="Security findings and vulnerability tracking" showAIButton={false} />
        <UnauthenticatedBanner feature="security findings" />
      </div>
    );
  }

  const findingsColumnCount = 5; // severity, title, status, location, chevron
  const vulnerabilitiesColumnCount = 5; // severity, identifier, title, exploited, chevron
  const runsColumnCount = 5; // status, name, findings, date, chevron

  let findingsBody: ReactNode;
  if (findingsGrid.isLoading) {
    findingsBody = <LoadingSkeleton rows={6} columns={findingsColumnCount} />;
  } else if (findingsGrid.isError) {
    findingsBody = <ErrorState message="Unable to load findings." onRetry={findingsGrid.refetch} />;
  } else if (findingsGrid.items.length === 0) {
    findingsBody = (
      <EmptyState
        icon={<BugRegular />}
        title="No Findings"
        description="Awaiting first scan. Once connected, LeftShield will detect secrets, vulnerabilities, and misconfigurations across your codebase. Connect a repository to begin."
      />
    );
  } else {
    findingsBody = (
      <ListLoadingOverlay busy={findingsGrid.isFetching && !findingsGrid.isLoading}>
        <Table aria-label="Security findings">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Severity</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortFieldRaw === 'title' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('title')}
                onClick={() => toggleSort('title')}
                style={{ width: '100%' }}
              >
                Title
              </TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Location</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(1) }} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {findingsGrid.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Badge appearance="filled" color={getSeverityColor(item.severity)}>
                    {item.severity}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
                    <Body1 style={{ fontWeight: 500 }}>{item.title}</Body1>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      {item.controlId} - {item.frameworkCode}
                    </Text>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={getStatusColor(item.status)}>
                    {item.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Body2 style={{ fontFamily: 'monospace' }}>
                    {item.filePath ? `${item.filePath}:${item.line}` : item.resourceId || '-'}
                  </Body2>
                </TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    icon={<ChevronRightRegular />}
                    aria-label="View finding details"
                    onClick={() => navigate(`/security/findings/${item.referenceId}`)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  let vulnerabilitiesBody: ReactNode;
  if (vulnerabilitiesGrid.isLoading) {
    vulnerabilitiesBody = <LoadingSkeleton rows={6} columns={vulnerabilitiesColumnCount} />;
  } else if (vulnerabilitiesGrid.isError) {
    vulnerabilitiesBody = (
      <ErrorState message="Unable to load vulnerabilities." onRetry={vulnerabilitiesGrid.refetch} />
    );
  } else if (vulnerabilitiesGrid.items.length === 0) {
    vulnerabilitiesBody = (
      <EmptyState
        icon={<WarningRegular />}
        title="No Vulnerabilities"
        description="No scans executed yet. Connect a repository to begin vulnerability scanning."
      />
    );
  } else {
    vulnerabilitiesBody = (
      <ListLoadingOverlay busy={vulnerabilitiesGrid.isFetching && !vulnerabilitiesGrid.isLoading}>
        <Table aria-label="Vulnerabilities">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Severity</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortFieldRaw === 'identifier' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('identifier')}
                onClick={() => toggleSort('identifier')}
              >
                Identifier
              </TableHeaderCell>
              <TableHeaderCell style={{ width: '100%' }}>Title</TableHeaderCell>
              <TableHeaderCell>Exploited</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(1) }} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {vulnerabilitiesGrid.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Badge appearance="filled" color={getSeverityColor(item.severity)}>
                    {item.severity}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Body1 style={{ fontFamily: 'monospace', fontWeight: 500 }}>{item.identifier}</Body1>
                </TableCell>
                <TableCell>
                  <div>
                    <Body1>{item.title}</Body1>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      {item.affectedPackageCount} affected package{item.affectedPackageCount !== 1 ? 's' : ''}
                    </Body2>
                  </div>
                </TableCell>
                <TableCell>
                  {item.isKnownExploited ? (
                    <Badge appearance="filled" color="danger">
                      Known Exploited
                    </Badge>
                  ) : (
                    <Body2>-</Body2>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    icon={<ChevronRightRegular />}
                    aria-label="View vulnerability details"
                    onClick={() => navigate(`/security/vulnerabilities/${item.referenceId}`)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  let runsBody: ReactNode;
  if (runsGrid.isLoading) {
    runsBody = <LoadingSkeleton rows={6} columns={runsColumnCount} />;
  } else if (runsGrid.isError) {
    runsBody = <ErrorState message="Unable to load scans." onRetry={runsGrid.refetch} />;
  } else if (runsGrid.items.length === 0) {
    runsBody = (
      <EmptyState
        icon={<ArrowSyncRegular />}
        title="No Scans Found"
        description="No scans found yet. Example: a policy run detects a secret committed in a PR, blocks the merge, and generates an evidence record. Trigger a scan to see results."
      />
    );
  } else {
    runsBody = (
      <ListLoadingOverlay busy={runsGrid.isFetching && !runsGrid.isLoading}>
        <Table aria-label="Scans">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortFieldRaw === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('name')}
                onClick={() => toggleSort('name')}
                style={{ width: '100%' }}
              >
                Scan Name
              </TableHeaderCell>
              <TableHeaderCell>Findings</TableHeaderCell>
              <TableHeaderCell>Date</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(1) }} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {runsGrid.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Badge
                    appearance="filled"
                    color={
                      item.status === 'Completed'
                        ? 'success'
                        : item.status === 'Failed'
                        ? 'danger'
                        : item.status === 'Running'
                        ? 'informative'
                        : 'warning'
                    }
                  >
                    {item.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div>
                    <Body1 style={{ fontWeight: 500 }}>{item.name}</Body1>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{item.targetIdentifier}</Body2>
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                    {item.criticalFindings > 0 && (
                      <Badge appearance="tint" color="danger">
                        {item.criticalFindings} critical
                      </Badge>
                    )}
                    {item.highFindings > 0 && (
                      <Badge appearance="tint" color="danger">
                        {item.highFindings} high
                      </Badge>
                    )}
                    <Body2>{item.totalFindings} total</Body2>
                  </div>
                </TableCell>
                <TableCell>
                  <Body2>{item.startedAt ? new Date(item.startedAt).toLocaleString() : '-'}</Body2>
                </TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    icon={<ChevronRightRegular />}
                    aria-label="View scan details"
                    onClick={() => navigate(`/runs/${item.referenceId}`)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  // Advanced Filters dialog trigger + surface — kept as a self-contained UI affordance (as in the
  // pre-migration page, these fields are not yet wired to the data queries). Rendered as the
  // toolbar's `primaryActions` slot since `ListToolbar` doesn't yet have a dedicated "advanced
  // filters dialog" slot distinct from its inline column-filter toggle — see journal flag below.
  const advancedFiltersDialog = (
    <Dialog open={isAdvancedFiltersOpen} onOpenChange={(_, data) => setIsAdvancedFiltersOpen(data.open)}>
      <DialogTrigger disableButtonEnhancement>
        <Button appearance="secondary" icon={<FilterRegular />}>
          More Filters
        </Button>
      </DialogTrigger>
      <DialogSurface style={{ maxWidth: '600px' }}>
        <DialogTitle>Advanced Filters</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Title3>Date Range</Title3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingHorizontalM }}>
                <Field label="From">
                  <Input
                    type="date"
                    value={advancedFilters.dateRangeFrom}
                    onChange={(_, data) => setAdvancedFilters({ ...advancedFilters, dateRangeFrom: data.value })}
                  />
                </Field>
                <Field label="To">
                  <Input
                    type="date"
                    value={advancedFilters.dateRangeTo}
                    onChange={(_, data) => setAdvancedFilters({ ...advancedFilters, dateRangeTo: data.value })}
                  />
                </Field>
              </div>

              <Divider />

              <Title3>Finding Types</Title3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingVerticalS }}>
                <Checkbox
                  label="SAST (Code vulnerabilities)"
                  checked={advancedFilters.findingTypes.includes('SAST')}
                  onChange={(_, data) => {
                    const types = data.checked
                      ? [...advancedFilters.findingTypes, 'SAST']
                      : advancedFilters.findingTypes.filter((t) => t !== 'SAST');
                    setAdvancedFilters({ ...advancedFilters, findingTypes: types });
                  }}
                />
                <Checkbox
                  label="SCA (Dependencies)"
                  checked={advancedFilters.findingTypes.includes('SCA')}
                  onChange={(_, data) => {
                    const types = data.checked
                      ? [...advancedFilters.findingTypes, 'SCA']
                      : advancedFilters.findingTypes.filter((t) => t !== 'SCA');
                    setAdvancedFilters({ ...advancedFilters, findingTypes: types });
                  }}
                />
                <Checkbox
                  label="Secrets"
                  checked={advancedFilters.findingTypes.includes('Secrets')}
                  onChange={(_, data) => {
                    const types = data.checked
                      ? [...advancedFilters.findingTypes, 'Secrets']
                      : advancedFilters.findingTypes.filter((t) => t !== 'Secrets');
                    setAdvancedFilters({ ...advancedFilters, findingTypes: types });
                  }}
                />
                <Checkbox
                  label="IaC (Infrastructure)"
                  checked={advancedFilters.findingTypes.includes('IaC')}
                  onChange={(_, data) => {
                    const types = data.checked
                      ? [...advancedFilters.findingTypes, 'IaC']
                      : advancedFilters.findingTypes.filter((t) => t !== 'IaC');
                    setAdvancedFilters({ ...advancedFilters, findingTypes: types });
                  }}
                />
                <Checkbox
                  label="Compliance"
                  checked={advancedFilters.findingTypes.includes('Compliance')}
                  onChange={(_, data) => {
                    const types = data.checked
                      ? [...advancedFilters.findingTypes, 'Compliance']
                      : advancedFilters.findingTypes.filter((t) => t !== 'Compliance');
                    setAdvancedFilters({ ...advancedFilters, findingTypes: types });
                  }}
                />
                <Checkbox
                  label="Cloud Posture"
                  checked={advancedFilters.findingTypes.includes('CloudPosture')}
                  onChange={(_, data) => {
                    const types = data.checked
                      ? [...advancedFilters.findingTypes, 'CloudPosture']
                      : advancedFilters.findingTypes.filter((t) => t !== 'CloudPosture');
                    setAdvancedFilters({ ...advancedFilters, findingTypes: types });
                  }}
                />
              </div>

              <Divider />

              <Title3>Remediation Status</Title3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
                <Checkbox
                  label="Has remediation available"
                  checked={advancedFilters.hasRemediation === true}
                  onChange={(_, data) =>
                    setAdvancedFilters({ ...advancedFilters, hasRemediation: data.checked ? true : null })
                  }
                />
                <Checkbox
                  label="Known exploited vulnerability (KEV)"
                  checked={advancedFilters.isKnownExploited === true}
                  onChange={(_, data) =>
                    setAdvancedFilters({ ...advancedFilters, isKnownExploited: data.checked ? true : null })
                  }
                />
              </div>

              <Divider />

              <Title3>Assignment</Title3>
              <Checkbox
                label="Assigned to me"
                checked={advancedFilters.assignedToMe}
                onChange={(_, data) => setAdvancedFilters({ ...advancedFilters, assignedToMe: data.checked === true })}
              />

              <Field label="Framework">
                <Dropdown
                  placeholder="All frameworks"
                  multiselect
                  onOptionSelect={(_, data) => {
                    setAdvancedFilters({ ...advancedFilters, frameworkCodes: data.selectedOptions });
                  }}
                >
                  <Option value="SOC2">SOC 2</Option>
                  <Option value="PCI-DSS">PCI-DSS</Option>
                  <Option value="HIPAA">HIPAA</Option>
                  <Option value="ISO27001">ISO 27001</Option>
                  <Option value="NIST">NIST CSF</Option>
                  <Option value="CIS">CIS Controls</Option>
                </Dropdown>
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={() => {
                setAdvancedFilters({
                  dateRangeFrom: '',
                  dateRangeTo: '',
                  frameworkCodes: [],
                  findingTypes: [],
                  repositories: [],
                  hasRemediation: null,
                  isKnownExploited: null,
                  assignedToMe: false,
                });
              }}
            >
              Reset
            </Button>
            <Button appearance="primary" onClick={() => setIsAdvancedFiltersOpen(false)}>
              Apply Filters
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );

  const severityDropdown = (
    <Dropdown
      key="severity"
      placeholder="Severity"
      value={severityFilter}
      onOptionSelect={(_, data) => handleSeverityFilterChange((data.optionValue || '') as Severity | '')}
      style={{ minWidth: '150px' }}
    >
      <Option value="">All Severities</Option>
      <Option value="Critical">Critical</Option>
      <Option value="High">High</Option>
      <Option value="Medium">Medium</Option>
      <Option value="Low">Low</Option>
    </Dropdown>
  );

  const statusDropdown = (
    <Dropdown
      key="status"
      placeholder="Status"
      value={statusFilter}
      onOptionSelect={(_, data) => handleStatusFilterChange((data.optionValue || '') as FindingStatus | '')}
      style={{ minWidth: '150px' }}
    >
      <Option value="">All Statuses</Option>
      <Option value="Open">Open</Option>
      <Option value="InProgress">In Progress</Option>
      <Option value="Resolved">Resolved</Option>
      <Option value="Suppressed">Suppressed</Option>
      <Option value="FalsePositive">False Positive</Option>
    </Dropdown>
  );

  // Severity/Status only filter real data on the Findings and Vulnerabilities tabs (the Scans
  // tab's data has no severity/status field) — omitted there rather than rendered inert, per the
  // List & Grid Standard's toolbar-filters-must-be-wired principle.
  const fkFilters =
    selectedTab === 'findings'
      ? [severityDropdown, statusDropdown]
      : selectedTab === 'vulnerabilities'
      ? [severityDropdown]
      : [];

  const searchPlaceholder =
    selectedTab === 'findings'
      ? 'Search findings…'
      : selectedTab === 'vulnerabilities'
      ? 'Search vulnerabilities…'
      : 'Search scans…';

  return (
    <div className={styles.page}>
      <PageHeader title="Security" subtitle="Security findings and vulnerability tracking" />

      {/* Summary Cards — non-list content, stays outside the scrolling list frame. */}
      <div className={ps.metricGrid}>
        <Card>
          <CardHeader
            image={<BugRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground1 }} />}
            header={<Body2>Total Findings</Body2>}
            description={<Title2>{dashboard?.totalFindings ?? 0}</Title2>}
          />
        </Card>
        <Card>
          <CardHeader
            image={<WarningRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground1 }} />}
            header={<Body2>Critical</Body2>}
            description={
              <Title2 style={{ color: tokens.colorPaletteRedForeground1 }}>
                {dashboard?.criticalFindings ?? 0}
              </Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<WarningRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground2 }} />}
            header={<Body2>High</Body2>}
            description={
              <Title2 style={{ color: tokens.colorPaletteRedForeground2 }}>
                {dashboard?.highFindings ?? 0}
              </Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<ShieldCheckmarkRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteGreenForeground1 }} />}
            header={<Body2>Open</Body2>}
            description={
              <Title2 style={{ color: tokens.colorNeutralForeground1 }}>
                {dashboard?.openFindings ?? 0}
              </Title2>
            }
          />
        </Card>
      </div>

      {/* Findings / Vulnerabilities / Scans list */}
      <div className={styles.listSection}>
        <ListPageLayout
          toolbar={
            <>
              <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
                <Tab value="findings" icon={<BugRegular />}>
                  Findings
                </Tab>
                <Tab value="vulnerabilities" icon={<WarningRegular />}>
                  Vulnerabilities
                </Tab>
                <Tab value="scans" icon={<ArrowSyncRegular />}>
                  Scans
                </Tab>
              </TabList>
              <ListToolbar
                primaryActions={advancedFiltersDialog}
                fkFilters={fkFilters}
                search={state.search}
                onSearchChange={setSearch}
                searchPlaceholder={searchPlaceholder}
                onRefresh={activeGrid.refetch}
                isRefreshing={activeGrid.isFetching}
                testIdPrefix="security"
              />
            </>
          }
          footer={
            <Pager
              page={state.page}
              totalPages={activeGrid.totalPages}
              hasPreviousPage={activeGrid.hasPreviousPage}
              hasNextPage={activeGrid.hasNextPage}
              totalCount={activeGrid.totalCount}
              onPageChange={setPage}
              testId="security-pager"
            />
          }
        >
          {selectedTab === 'findings' ? findingsBody : selectedTab === 'vulnerabilities' ? vulnerabilitiesBody : runsBody}
        </ListPageLayout>
      </div>

    </div>
  );
};
